import {
  RUNTIME_SCOPE_CAMPAIGN_SANDBOX,
  STORAGE_MODE_LOCAL_SANDBOX,
  buildSandboxCampaignId,
  normalizeCampaignSandboxSeedId,
  normalizeCampaignSandboxSourceMode,
  normalizeSandboxStateChars,
} from '../campaignSandboxContract.js';

export const CAMPAIGN_SANDBOX_STORAGE_VERSION = 1;
export const CAMPAIGN_SANDBOX_STORAGE_INDEX_KEY = 'bmg:sandbox:v1:index';
export const CAMPAIGN_SANDBOX_STORAGE_KEY_PREFIX = 'bmg:sandbox:v1:';
export const CAMPAIGN_SANDBOX_MAX_SAVED_SANDBOXES = 12;

// Storage keys use the same slug normalization as seed builders / char-id
// prefixes (campaignSandboxContract). Previously this module kept its own
// looser normalizer (raw trim, 128 chars) — two normalizers meant a sandboxId
// could produce a storage key that char-row matching didn't agree with.
export const normalizeCampaignSandboxId = (value) => normalizeCampaignSandboxSeedId(value);

const readBrowserLocalStorage = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch (_) {
    return null;
  }
};

const safeJsonParse = (raw, fallback = null) => {
  if (!raw || typeof raw !== 'string') return fallback;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return fallback;
  }
};

export const isCampaignSandboxStorageConfig = (config = {}) => {
  const launch = config.initialLaunch && typeof config.initialLaunch === 'object'
    ? config.initialLaunch
    : {};
  return (
    config.runtimeScope === RUNTIME_SCOPE_CAMPAIGN_SANDBOX
    || config.storageMode === STORAGE_MODE_LOCAL_SANDBOX
    || launch.runtimeScope === RUNTIME_SCOPE_CAMPAIGN_SANDBOX
    || launch.storageMode === STORAGE_MODE_LOCAL_SANDBOX
  );
};

export const getCampaignSandboxIdFromConfig = (config = {}) => {
  const launch = config.initialLaunch && typeof config.initialLaunch === 'object'
    ? config.initialLaunch
    : {};
  return normalizeCampaignSandboxId(config.sandboxId || launch.sandboxId);
};

export const buildCampaignSandboxStorageKey = (sandboxId) => (
  `${CAMPAIGN_SANDBOX_STORAGE_KEY_PREFIX}${encodeURIComponent(normalizeCampaignSandboxId(sandboxId))}`
);

const getCampaignSandboxIdFromStorageKey = (key) => {
  if (key === CAMPAIGN_SANDBOX_STORAGE_INDEX_KEY) return null;
  if (typeof key !== 'string' || !key.startsWith(CAMPAIGN_SANDBOX_STORAGE_KEY_PREFIX)) return null;
  try {
    return normalizeCampaignSandboxId(decodeURIComponent(key.slice(CAMPAIGN_SANDBOX_STORAGE_KEY_PREFIX.length)));
  } catch (_) {
    return null;
  }
};

export const buildCampaignSandboxIndexKey = () => CAMPAIGN_SANDBOX_STORAGE_INDEX_KEY;

export const isCampaignSandboxStorageKey = (key) => (
  key === CAMPAIGN_SANDBOX_STORAGE_INDEX_KEY
  || (typeof key === 'string' && key.startsWith(CAMPAIGN_SANDBOX_STORAGE_KEY_PREFIX))
);

export const buildCampaignSandboxMetadataFromConfig = (config = {}) => {
  const launch = config.initialLaunch && typeof config.initialLaunch === 'object'
    ? config.initialLaunch
    : {};
  const sandboxId = getCampaignSandboxIdFromConfig(config);
  const sourceCampaignId = config.sourceCampaignId || launch.sourceCampaignId || null;
  const sourceMode = normalizeCampaignSandboxSourceMode(config.sourceMode || launch.sourceMode);
  return {
    sandboxId,
    sourceCampaignId,
    sourceMode,
    label: typeof config.sandboxLabel === 'string' && config.sandboxLabel.trim()
      ? config.sandboxLabel.trim().slice(0, 120)
      : null,
  };
};

const normalizeIndex = (value) => (
  value && typeof value === 'object' && value.version === CAMPAIGN_SANDBOX_STORAGE_VERSION
    && value.sandboxes && typeof value.sandboxes === 'object'
    ? value
    : { version: CAMPAIGN_SANDBOX_STORAGE_VERSION, sandboxes: {} }
);

export const loadCampaignSandboxIndex = () => {
  const storage = readBrowserLocalStorage();
  if (!storage) return { version: CAMPAIGN_SANDBOX_STORAGE_VERSION, sandboxes: {} };
  return normalizeIndex(safeJsonParse(storage.getItem(CAMPAIGN_SANDBOX_STORAGE_INDEX_KEY)));
};

const saveCampaignSandboxIndex = (storage, index) => {
  storage.setItem(CAMPAIGN_SANDBOX_STORAGE_INDEX_KEY, JSON.stringify(normalizeIndex(index)));
};

const pruneCampaignSandboxIndex = (
  storage,
  index,
  currentSandboxId,
  maxSavedSandboxes = CAMPAIGN_SANDBOX_MAX_SAVED_SANDBOXES,
) => {
  const normalized = normalizeIndex(index);
  const entries = Object.entries(normalized.sandboxes || {});
  if (entries.length <= maxSavedSandboxes) return normalized;

  const currentId = normalizeCampaignSandboxId(currentSandboxId);
  const sorted = entries
    .sort(([, a], [, b]) => (Number(b?.updatedAt) || 0) - (Number(a?.updatedAt) || 0));
  const keptIds = new Set([currentId]);
  sorted.forEach(([sandboxId]) => {
    if (keptIds.size < maxSavedSandboxes) keptIds.add(sandboxId);
  });

  const sandboxes = {};
  entries.forEach(([sandboxId, metadata]) => {
    if (keptIds.has(sandboxId)) {
      sandboxes[sandboxId] = metadata;
    } else {
      storage.removeItem(buildCampaignSandboxStorageKey(sandboxId));
    }
  });
  return { ...normalized, sandboxes };
};

const listStoredCampaignSandboxIds = (storage) => {
  const ids = [];
  try {
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      const sandboxId = getCampaignSandboxIdFromStorageKey(key);
      if (sandboxId) ids.push(sandboxId);
    }
  } catch (_) {
    return ids;
  }
  return ids;
};

const isStorageQuotaError = (error) => (
  !!error
  && (
    error.name === 'QuotaExceededError'
    || error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || error.code === 22
    || error.code === 1014
  )
);

const pruneOldestSandboxForQuotaRetry = (storage, index, currentSandboxId) => {
  const normalized = normalizeIndex(index);
  const currentId = normalizeCampaignSandboxId(currentSandboxId);
  const indexed = new Map(Object.entries(normalized.sandboxes || {}));
  listStoredCampaignSandboxIds(storage).forEach((sandboxId) => {
    if (!indexed.has(sandboxId)) {
      indexed.set(sandboxId, {
        sandboxId,
        updatedAt: 0,
      });
    }
  });
  const removable = [...indexed.entries()]
    .filter(([sandboxId]) => sandboxId !== currentId)
    .sort(([, a], [, b]) => (Number(a?.updatedAt) || 0) - (Number(b?.updatedAt) || 0));
  if (removable.length === 0) return { index: normalized, removed: false };

  const [sandboxIdToRemove] = removable[0];
  storage.removeItem(buildCampaignSandboxStorageKey(sandboxIdToRemove));
  const sandboxes = { ...(normalized.sandboxes || {}) };
  delete sandboxes[sandboxIdToRemove];
  return { index: { ...normalized, sandboxes }, removed: true };
};

const cloneJson = (value) => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_) {
    return value;
  }
};

// Normalize a snapshot's chars collection through the shared contract helper:
// drop rows that don't belong to this sandbox, strip live-authority fields,
// re-stamp identity. Runs on every load and import so hand-edited or stale
// localStorage can't reintroduce authority fields the seed builders stripped.
const normalizeSandboxSnapshotState = (state, sandboxId) => {
  if (!state || typeof state !== 'object') return state;
  if (!state.chars || typeof state.chars !== 'object') return state;
  return {
    ...state,
    chars: normalizeSandboxStateChars(
      state.chars,
      sandboxId,
      state.globalSettings?.campaignId || null,
    ),
  };
};

const isSandboxCharacterLike = (value) => (
  value
  && typeof value === 'object'
  && (
    value.isSandboxCharacter === true
    || value.campaignCharacterKind === 'sandbox'
  )
);

const retargetSandboxCharacter = (character, sandboxId = 'default') => ({
  ...character,
  campaignId: buildSandboxCampaignId(sandboxId),
  sandboxId: normalizeCampaignSandboxId(sandboxId),
  isSandboxCharacter: true,
  campaignCharacterKind: 'sandbox',
});

const retargetSandboxMetadataDeep = (value, sandboxId = 'default') => {
  const normalizedSandboxId = normalizeCampaignSandboxId(sandboxId);
  if (Array.isArray(value)) {
    return value.map(entry => retargetSandboxMetadataDeep(entry, normalizedSandboxId));
  }
  if (!value || typeof value !== 'object') return value;
  const base = isSandboxCharacterLike(value)
    ? retargetSandboxCharacter(value, normalizedSandboxId)
    : { ...value };
  return Object.fromEntries(
    Object.entries(base).map(([key, innerValue]) => [
      key,
      key === 'sandboxId'
        ? normalizedSandboxId
        : retargetSandboxMetadataDeep(innerValue, normalizedSandboxId),
    ]),
  );
};

const retargetSandboxState = (state = {}, sandboxId = 'default') => {
  const normalizedSandboxId = normalizeCampaignSandboxId(sandboxId);
  const nextState = retargetSandboxMetadataDeep(cloneJson(state), normalizedSandboxId);
  if (!nextState || typeof nextState !== 'object') return state;
  if (nextState.globalSettings && typeof nextState.globalSettings === 'object') {
    nextState.globalSettings = {
      ...nextState.globalSettings,
      campaignSandbox: true,
      campaignId: buildSandboxCampaignId(normalizedSandboxId),
    };
  }
  return nextState;
};

export const loadCampaignSandboxState = (config = {}, fallbackState = {}) => {
  if (!isCampaignSandboxStorageConfig(config)) return fallbackState;
  const storage = readBrowserLocalStorage();
  if (!storage) return fallbackState;
  const sandboxId = getCampaignSandboxIdFromConfig(config);
  const snapshot = safeJsonParse(storage.getItem(buildCampaignSandboxStorageKey(sandboxId)));
  if (
    !snapshot
    || snapshot.version !== CAMPAIGN_SANDBOX_STORAGE_VERSION
    || !snapshot.state
    || typeof snapshot.state !== 'object'
  ) {
    return fallbackState;
  }
  // Every consumer (useStorage initial state, App's runtime-settings read,
  // the local driver) sees the same sanitized shape — never raw localStorage.
  return normalizeSandboxSnapshotState(snapshot.state, sandboxId);
};

export const saveCampaignSandboxState = (config = {}, state = {}) => {
  if (!isCampaignSandboxStorageConfig(config)) return false;
  const storage = readBrowserLocalStorage();
  if (!storage) return false;
  const metadata = buildCampaignSandboxMetadataFromConfig(config);
  const now = Date.now();
  const snapshot = {
    version: CAMPAIGN_SANDBOX_STORAGE_VERSION,
    sandboxId: metadata.sandboxId,
    metadata,
    updatedAt: now,
    state,
  };
  const snapshotKey = buildCampaignSandboxStorageKey(metadata.sandboxId);
  const snapshotJson = JSON.stringify(snapshot);
  const previousSnapshotJson = storage.getItem(snapshotKey);
  const rollbackSnapshotWrite = () => {
    try {
      if (previousSnapshotJson === null) {
        storage.removeItem(snapshotKey);
      } else {
        storage.setItem(snapshotKey, previousSnapshotJson);
      }
    } catch (_) {
      // Best effort only. The caller still receives a failed persistence result.
    }
  };
  let retryIndex = loadCampaignSandboxIndex();
  let snapshotWritten = false;
  try {
    for (let attempt = 0; attempt <= CAMPAIGN_SANDBOX_MAX_SAVED_SANDBOXES + 4; attempt += 1) {
      try {
        storage.setItem(snapshotKey, snapshotJson);
        snapshotWritten = true;
        break;
      } catch (error) {
        if (!isStorageQuotaError(error)) throw error;
        const prunedForRetry = pruneOldestSandboxForQuotaRetry(storage, retryIndex, metadata.sandboxId);
        if (!prunedForRetry.removed) throw error;
        retryIndex = prunedForRetry.index;
        try {
          saveCampaignSandboxIndex(storage, retryIndex);
        } catch (indexError) {
          if (!isStorageQuotaError(indexError)) throw indexError;
        }
      }
    }
    if (!snapshotWritten) return false;
    const index = loadCampaignSandboxIndex();
    const previous = index.sandboxes[metadata.sandboxId] || {};
    const nextIndex = {
      ...index,
      sandboxes: {
        ...index.sandboxes,
        [metadata.sandboxId]: {
          ...previous,
          ...metadata,
          createdAt: previous.createdAt || now,
          updatedAt: now,
        },
      },
    };
    try {
      saveCampaignSandboxIndex(storage, pruneCampaignSandboxIndex(storage, nextIndex, metadata.sandboxId));
    } catch (indexError) {
      rollbackSnapshotWrite();
      throw indexError;
    }
    return true;
  } catch (error) {
    console.warn('Campaign sandbox local persistence failed:', error);
    return false;
  }
};

export const resetCampaignSandboxState = (sandboxId = 'default') => {
  const storage = readBrowserLocalStorage();
  if (!storage) return false;
  const normalizedSandboxId = normalizeCampaignSandboxId(sandboxId);
  try {
    storage.removeItem(buildCampaignSandboxStorageKey(normalizedSandboxId));
    const index = loadCampaignSandboxIndex();
    if (index.sandboxes[normalizedSandboxId]) {
      const nextSandboxes = { ...index.sandboxes };
      delete nextSandboxes[normalizedSandboxId];
      saveCampaignSandboxIndex(storage, { ...index, sandboxes: nextSandboxes });
    }
    return true;
  } catch (error) {
    console.warn('Campaign sandbox reset failed:', error);
    return false;
  }
};

export const exportCampaignSandboxSnapshot = (sandboxId = 'default') => {
  const storage = readBrowserLocalStorage();
  if (!storage) return null;
  const snapshot = safeJsonParse(storage.getItem(buildCampaignSandboxStorageKey(sandboxId)));
  if (
    !snapshot
    || snapshot.version !== CAMPAIGN_SANDBOX_STORAGE_VERSION
    || !snapshot.state
    || typeof snapshot.state !== 'object'
  ) {
    return null;
  }
  return snapshot;
};

export const importCampaignSandboxSnapshot = (sandboxId = 'default', snapshotOrJson = null, metadataOverrides = {}) => {
  const snapshot = typeof snapshotOrJson === 'string'
    ? safeJsonParse(snapshotOrJson)
    : snapshotOrJson;
  if (
    !snapshot
    || typeof snapshot !== 'object'
    || snapshot.version !== CAMPAIGN_SANDBOX_STORAGE_VERSION
    || !snapshot.state
    || typeof snapshot.state !== 'object'
  ) {
    return false;
  }
  const metadata = snapshot.metadata && typeof snapshot.metadata === 'object'
    ? snapshot.metadata
    : {};
  return saveCampaignSandboxState({
    runtimeScope: RUNTIME_SCOPE_CAMPAIGN_SANDBOX,
    storageMode: STORAGE_MODE_LOCAL_SANDBOX,
    sandboxId,
    sourceCampaignId: metadataOverrides.sourceCampaignId ?? metadata.sourceCampaignId ?? null,
    sourceMode: metadataOverrides.sourceMode ?? metadata.sourceMode ?? null,
    sandboxLabel: metadataOverrides.label ?? metadata.label ?? null,
  }, normalizeSandboxSnapshotState(retargetSandboxState(snapshot.state, sandboxId), sandboxId));
};

export const duplicateCampaignSandboxState = (sourceSandboxId, targetSandboxId, metadataOverrides = {}) => {
  const sourceSnapshot = exportCampaignSandboxSnapshot(sourceSandboxId);
  if (!sourceSnapshot) return false;
  return importCampaignSandboxSnapshot(targetSandboxId, sourceSnapshot, {
    ...metadataOverrides,
    sourceMode: metadataOverrides.sourceMode || sourceSnapshot.metadata?.sourceMode || 'blank',
  });
};
